import React from "react";
import { motion } from "framer-motion";
import {
  School,
  FileText,
  Users,
  PenTool,
  Shield,
  GraduationCap,
  MapPin,
  Scale,
  Sun,
  Compass,
  Building2,
  Landmark,
} from "lucide-react";

const services = [
  {
    icon: School,
    title: "School Shortlisting",
    description:
      "Strategic curation of schools matched to your child's profile, ambitions, and family values.",
  },
  {
    icon: FileText,
    title: "UKiset & Test Guidance",
    description:
      "Expert preparation for UKiset, ISEB Pre-test, CAT4, and school-specific entrance assessments.",
  },
  {
    icon: Users,
    title: "Interview Coaching",
    description:
      "One-to-one sessions to build confidence, articulation, and authentic self-presentation.",
  },
  {
    icon: PenTool,
    title: "Essay & Statement Support",
    description:
      "Crafting compelling personal statements that reflect genuine character and aspirations.",
  },
  {
    icon: Shield,
    title: "Guardianship Guidance",
    description:
      "Trusted recommendations for guardianship services in the UK and other destinations.",
  },
  {
    icon: GraduationCap,
    title: "Scholarship Strategy",
    description:
      "Identifying and positioning for academic, music, sport, and other merit-based awards.",
  },
  {
    icon: MapPin,
    title: "Visit Planning",
    description:
      "Coordinating and optimising school visits to help you make informed, confident decisions.",
  },
  {
    icon: Scale,
    title: "Post-Offer Decision Support",
    description:
      "Comparing offers, negotiating terms, and ensuring the final choice is the right one.",
  },
  {
    icon: Sun,
    title: "Summer School Advisory",
    description:
      "Selecting prestigious summer programmes that build skills, experience, and school connections.",
  },
  {
    icon: Compass,
    title: "Immersion Programmes",
    description:
      "Curated academic and cultural immersion experiences to prepare students for boarding life.",
  },
  {
    icon: Building2,
    title: "Indian Boarding Schools",
    description:
      "Expert guidance for admission to India's premier boarding schools — Doon, Mayo, Welham, Daly College, and more.",
  },
  {
    icon: Landmark,
    title: "British Schools in India",
    description:
      "Advisory for British-style boarding schools now in India — Harrow, Shrewsbury, Bedford, and Queen Elizabeth's Boys.",
  },
];

const Services = () => {
  return (
    <section id="services" className="py-16 md:py-20 lg:py-28 bg-[#F5F5F0]">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="max-w-3xl mx-auto text-center mb-10 md:mb-16"
        >
          <p className="text-xs sm:text-sm uppercase tracking-[0.2em] font-semibold text-[#C9A227] mb-4">
            What We Offer
          </p>
          <h2
            className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-medium text-[#1A202C] tracking-tight mb-4 sm:mb-6"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Comprehensive Advisory{" "}
            <span className="text-[#2B4C7E]">Services</span>
          </h2>
          <p className="text-base sm:text-lg text-[#4A5568] leading-relaxed">
            Every engagement is tailored to your family's specific needs — not a
            one-size-fits-all package.
          </p>
        </motion.div>

        {/* Bento Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {services.map((service, index) => {
            const Icon = service.icon;
            
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.05 * index }}
                className="service-card bg-white border border-[#E2E8F0] p-5 sm:p-6 rounded-[4px] group cursor-default"
                data-testid={`service-card-${index}`}
              >
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-[#2B4C7E]/10 flex items-center justify-center mb-3 sm:mb-4 group-hover:bg-[#C9A227]/20 transition-colors duration-300">
                  <Icon className="w-5 h-5 sm:w-6 sm:h-6 text-[#2B4C7E] group-hover:text-[#C9A227] transition-colors duration-300" />
                </div>
                <h3
                  className="text-base sm:text-lg font-medium text-[#1A202C] mb-2"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  {service.title}
                </h3>
                <p className="text-xs sm:text-sm text-[#4A5568] leading-relaxed">
                  {service.description}
                </p>
              </motion.div>
            );
          })}
        </div>

        {/* Custom Note */}
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="text-center text-sm sm:text-base text-[#4A5568] mt-8 md:mt-10 italic px-4"
        >
          Services are customised based on your family's requirements and timeline
          — we design a plan that works for you.
        </motion.p>
      </div>
    </section>
  );
};

export default Services;
